/*
 * FAQ page behaviour — two cooperating custom elements that sync via a shared
 * category string typed on both the nav strip and each FAQ item.
 *
 *   <faq-category-nav>  the icon strip. Clicking an item broadcasts the chosen
 *                       category on `document` as a `faq:select` CustomEvent.
 *   <faq-help>          the 2-column help section. Listens for `faq:select` and
 *                       shows only the FAQ items whose category matches, swapping
 *                       the left heading to the active label.
 *
 * Matching is by a slug (lowercased, non-alphanumerics collapsed to "-") so the
 * merchant only has to type the same word ("Delivery") on both sides.
 *
 * Load-order is unknown (the two sections are independent), so the active choice
 * is mirrored on `window.__faqActive`: the nav writes it on init, the help reads
 * it on connect, and both also listen for future events. Custom elements re-run
 * connectedCallback on `shopify:section:load`, so the theme editor stays in sync.
 */
(function () {
  const EVENT = 'faq:select';

  function slugify(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function titleCase(slug) {
    return String(slug || '')
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  function broadcast(detail) {
    window.__faqActive = detail;
    document.dispatchEvent(new CustomEvent(EVENT, { detail }));
  }

  class FaqCategoryNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-faq-category]'));
      if (!this.items.length) return;

      this.onClick = this.onClick.bind(this);
      this.onSelect = this.onSelect.bind(this);
      this.items.forEach((item) => item.addEventListener('click', this.onClick));
      document.addEventListener(EVENT, this.onSelect);

      // Adopt an existing active choice (e.g. another nav, a deep link) or default
      // to the first item — and broadcast it so the help section can sync.
      const existing = window.__faqActive;
      const adopt = existing && this.items.find((i) => i.dataset.slug === existing.slug);
      if (adopt) {
        this.markActive(adopt.dataset.slug);
      } else {
        this.select(this.items[0]);
      }
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.onSelect);
    }

    onClick(event) {
      this.select(event.currentTarget);
    }

    select(item) {
      const slug = item.dataset.slug || slugify(item.dataset.faqCategory);
      const label = (item.dataset.faqLabel || item.textContent || '').trim();
      this.markActive(slug);
      broadcast({ category: item.dataset.faqCategory, label, slug });
    }

    onSelect(event) {
      // Reflect external selections without re-broadcasting (avoids loops).
      this.markActive(event.detail && event.detail.slug);
    }

    markActive(slug) {
      this.items.forEach((item) => {
        const active = item.dataset.slug === slug;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
  }

  class FaqHelp extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-faq-category]'));
      this.heading = this.querySelector('[data-faq-heading]');
      this.onSelect = this.onSelect.bind(this);
      document.addEventListener(EVENT, this.onSelect);

      const active = window.__faqActive;
      if (active && active.slug) {
        this.apply(active.slug, active.label);
      } else if (this.items.length) {
        // No nav drove a choice — fall back to the first item's category.
        const slug = this.items[0].dataset.slug;
        this.apply(slug, titleCase(slug));
      }
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this.onSelect);
    }

    onSelect(event) {
      if (!event.detail) return;
      this.apply(event.detail.slug, event.detail.label);
    }

    apply(slug, label) {
      let shown = 0;
      this.items.forEach((item) => {
        const match = item.dataset.slug === slug;
        item.hidden = !match;
        if (match) shown += 1;
      });
      // If nothing matches the requested category, leave the items as-is rather
      // than blanking the column (guards against a typo'd nav/item pairing).
      if (!shown && this.items.length) {
        this.items.forEach((item) => (item.hidden = false));
      }
      if (this.heading) {
        this.heading.textContent = label || titleCase(slug);
      }
    }
  }

  if (!customElements.get('faq-category-nav')) {
    customElements.define('faq-category-nav', FaqCategoryNav);
  }
  if (!customElements.get('faq-help')) {
    customElements.define('faq-help', FaqHelp);
  }
})();
